import Section from '@widgets/components/section';
import SectionHeader from '@widgets/components/section-header';
import { useEffect, useMemo, useState } from 'react';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import { StatisticsIndicator } from 'pages/Main/components/stat-indicator';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import { useFilters, useListParamsAndPersistToUrl } from '@app/hooks/useList';
import { useUrlParams } from '@app/hooks/useUrlParams';
import { useNominalPower } from '@app/queries/hosting/useNominalPower';
import { useActualPower } from '@app/queries/hosting/useActualPower';
import { useAverageReward } from '@app/queries/hosting/useAverageReward';
import { ClientsIndicator } from '@pages/Hosting/components/ClientsIndicator';
import { useClientsIndicator } from '@app/queries/hosting/useClientsIndicator';
import { IClientsIndicator } from '@entities/hosting/IClientsIndicator';
import { HostingLostIndicator } from '@pages/Hosting/components/HostingLostIndicator';
import { RewardIndicator } from '@pages/Hosting/components/RewardIndicator';
import { HostingIndicator } from '@pages/Hosting/components/HostingIndicator';
import HostingChart from '@pages/Hosting/components/HostingChart';
import HostingIndicatorsFilter, { IHostingIndicatorsFilter } from '@pages/Hosting/Main/filter';
import ClientsChart from '@pages/Hosting/components/HostingLostIndicator2';

const HostingIndicators = () => {
  const dispatch = useDispatch();

  const nominalPowerFetcher = useNominalPower();
  const actualPowerFetcher = useActualPower();
  const averageRewardFetcher = useAverageReward();
  const clientsIndicatorFetcher = useClientsIndicator();

  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  const [nominalPower, setNominalPower] = useState<{
    value: number;
    percent: number;
  } | null>(null);
  const [actualPower, setActualPower] = useState<{
    value: number;
    percent: number;
    percent_diff: number;
  } | null>(null);
  const [averageReward, setAverageReward] = useState<{
    value: number;
    percent: number;
  } | null>(null);
  const [clientsIndicator, setClientsIndicator] = useState<IClientsIndicator | null>(null);

  const { urlParams } = useUrlParams();

  const filtering = useFilters(urlParams, []);
  useListParamsAndPersistToUrl(filtering);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Хостинг',
          type: BreadCrumbType.LINK,
          link: '/hosting/main',
          icon: BreadCrumbIcon.HOSTING,
        },
        { title: 'Основные показатели', type: BreadCrumbType.TEXT },
      ])
    );
  }, []);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const nominalPower = await nominalPowerFetcher.fetch(filtering.value);
      setNominalPower({
        value: nominalPower.nominal_power,
        percent: nominalPower.percent_change,
      });
    })();
  }, [filtering.value]);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const actualPower = await actualPowerFetcher.fetch(filtering.value);
      setActualPower({
        value: actualPower.actual_power,
        percent: actualPower.percent,
        percent_diff: actualPower.percent_change,
      });
    })();
  }, [filtering.value]);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const reward = await averageRewardFetcher.fetch(filtering.value);
      setAverageReward({
        value: reward.reward,
        percent: reward.percent_change,
      });
    })();
  }, [filtering.value]);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const clientsData = await clientsIndicatorFetcher.fetch(filtering.value);
      setClientsIndicator(clientsData);
    })();
  }, [filtering.value]);

  return (
    <>
      <HostingIndicatorsFilter
        filters={filtering.value}
        onChangeFilters={(values: IHostingIndicatorsFilter) => filtering.setValue(values)}
      />
      <div className="flex mb-8 gap-[10px]">
        <div className="grow">
          <Section>
            <div className="mb-[30px]">
              <SectionHeader title="График динамики" />
            </div>
            <HostingChart filters={filtering.value} />
          </Section>
        </div>
        <div className="flex flex-col shrink-0 basis-[320px] gap-[10px]">
          <div className="h-full border border-opacity-30 solid rounded-[16px] border-[#959595]">
            <RewardIndicator filter={filtering.value} />
          </div>
          <div className="h-full border border-opacity-30 solid rounded-[16px] border-[#959595]">
            <HostingIndicator filter={filtering.value} />
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-[11px]">
        <StatisticsIndicator
          id="nominalPower"
          title="Номинальная мощность"
          value={
            !nominalPowerFetcher.isPending &&
            nominalPower !== null &&
            nominalPower.value !== null ? (
              <div className="text-[32px]">{nominalPower.value} МВт</div>
            ) : (
              '-'
            )
          }
          percentage={
            !nominalPowerFetcher.isPending && nominalPower ? nominalPower?.percent : undefined
          }
          mode={nominalPower && nominalPower?.percent > 0 ? 'up' : 'down'}
          loading={
            nominalPowerFetcher.isPending || nominalPower === null || nominalPower.value === null
          }
        />

        <StatisticsIndicator
          id="actualPower"
          title="Фактическая мощность"
          value={
            !actualPowerFetcher.isPending && actualPower !== null && actualPower.value !== null ? (
              <div className="text-[32px]">
                {actualPower.value} МВт
                <span className="pl-[5px] text-[#373539] text-[14px]">{actualPower.percent} %</span>
              </div>
            ) : (
              '-'
            )
          }
          percentage={
            !actualPowerFetcher.isPending && actualPower ? actualPower?.percent : undefined
          }
          mode={actualPower && actualPower?.percent > 0 ? 'up' : 'down'}
          loading={
            actualPowerFetcher.isPending || actualPower === null || actualPower.value === null
          }
        />

        <ClientsIndicator
          data={clientsIndicator}
          loading={clientsIndicatorFetcher.isPending || clientsIndicator === null}
        />

        <HostingLostIndicator filter={filtering.value} />

        <StatisticsIndicator
          id="averageReward"
          title="Ср. доходность на кВт"
          value={
            !averageRewardFetcher.isPending &&
            averageReward !== null &&
            averageReward.value !== null ? (
              <div className="text-[32px]">{averageReward.value} RUB</div>
            ) : (
              '-'
            )
          }
          percentage={
            !averageRewardFetcher.isPending && averageReward ? averageReward?.percent : undefined
          }
          mode={averageReward && averageReward?.percent > 0 ? 'up' : 'down'}
          loading={
            averageRewardFetcher.isPending || averageReward === null || averageReward.value === null
          }
        />
      </div>
    </>
  );
};

export default HostingIndicators;
